import type {
  ChainLpcLppSupplierDailyTrendPoint,
  ChainLpcLppSupplierPropertyGroup,
  ChainLpcLppSupplierRollup,
} from '~/types/services/priceParrot';

import { HOTSPOT_RATIO_THRESHOLD, PERIOD_PRESETS } from './constants';
import { computeRatio } from './ratio';

export function hasLppSupplier(group: ChainLpcLppSupplierPropertyGroup): boolean {
  return group.rows.some((r) => r.channel === 'LPP');
}

/** Title-cases a lowercase supplier token (e.g. 'hotelbeds' -> 'Hotelbeds', 'w2m' -> 'W2m'). */
export function displaySupplier(supplier: string): string {
  if (supplier === 'LPC') {
    return supplier;
  }
  return supplier.charAt(0).toUpperCase() + supplier.slice(1);
}

export function emptyStateMessage(mixedSppOnly: boolean, hotspotsOnly: boolean, region: string | null): string {
  const activeFilters: Array<string> = [];
  if (mixedSppOnly) {
    activeFilters.push('both LPC and LPP bookings');
  }
  if (hotspotsOnly) {
    activeFilters.push(`a ratio below ${HOTSPOT_RATIO_THRESHOLD}`);
  }
  if (region) {
    activeFilters.push(`the "${region}" region`);
  }
  if (activeFilters.length === 0) {
    return 'No properties for this chain in the selected range.';
  }
  return `No properties matching ${activeFilters.join(' and ')} for this chain in the selected range.`;
}

export function computeRollup(properties: ReadonlyArray<ChainLpcLppSupplierPropertyGroup>): ChainLpcLppSupplierRollup {
  let bookingsCount = 0;
  let marginArAud = 0;
  let lpcViews: number | null = null;
  let lppViews: number | null = null;
  for (const group of properties) {
    for (const row of group.rows) {
      bookingsCount += row.bookings_count;
      marginArAud += row.margin_ar_aud;
    }
    if (group.lpc_views != null) {
      lpcViews = (lpcViews ?? 0) + group.lpc_views;
    }
    if (group.lpp_views != null) {
      lppViews = (lppViews ?? 0) + group.lpp_views;
    }
  }
  return {
    bookings_count: bookingsCount,
    margin_ar_aud: marginArAud,
    avg_margin_per_booking_aud: bookingsCount > 0 ? marginArAud / bookingsCount : null,
    lpc_views: lpcViews,
    lpp_views: lppViews,
  };
}

/** One supplier's chain-wide bookings/margin, and its bookings as a share of the chain's total LPC bookings. */
export type ChainSupplierRollupRow = {
  supplier: string;
  channel: 'LPC' | 'LPP';
  bookingsCount: number;
  marginArAud: number;
  avgMarginPerBookingAud: number | null;
  pctOfChainLpc: number | null;
};

export type ChainRollupOverview = {
  rollup: ChainLpcLppSupplierRollup;
  pctLpc: number | null;
  pctLpp: number | null;
  suppliers: Array<ChainSupplierRollupRow>;
};

/**
 * Chain-wide %LPC vs %LPP, plus each supplier's bookings as a share of chain
 * LPC bookings — SupplierRatioTable only has this per property.
 */
export function computeChainRollupOverview(
  properties: ReadonlyArray<ChainLpcLppSupplierPropertyGroup>,
): ChainRollupOverview {
  const rollup = computeRollup(properties);
  const pctLpc = computeRatio(rollup.lpc_views ?? 0, (rollup.lpc_views ?? 0) + (rollup.lpp_views ?? 0));
  const pctLpp = computeRatio(rollup.lpp_views ?? 0, (rollup.lpc_views ?? 0) + (rollup.lpp_views ?? 0));

  type SupplierEntry = { supplier: string; channel: 'LPC' | 'LPP'; bookingsCount: number; marginArAud: number };
  const bySupplier = new Map<string, SupplierEntry>();
  let lpcBookingsTotal = 0;
  for (const group of properties) {
    for (const row of group.rows) {
      const key = `${row.channel}-${row.supplier}`;
      const entry = bySupplier.get(key) ?? {
        supplier: displaySupplier(row.supplier),
        channel: row.channel,
        bookingsCount: 0,
        marginArAud: 0,
      };
      entry.bookingsCount += row.bookings_count;
      entry.marginArAud += row.margin_ar_aud;
      bySupplier.set(key, entry);
      if (row.channel === 'LPC') {
        lpcBookingsTotal += row.bookings_count;
      }
    }
  }

  const suppliers = Array.from(bySupplier.values())
    .map((entry) => ({
      supplier: entry.supplier,
      channel: entry.channel,
      bookingsCount: entry.bookingsCount,
      marginArAud: entry.marginArAud,
      avgMarginPerBookingAud: entry.bookingsCount > 0 ? entry.marginArAud / entry.bookingsCount : null,
      pctOfChainLpc: entry.channel === 'LPC' ? null : computeRatio(entry.bookingsCount, lpcBookingsTotal),
    }))
    .sort((a, b) => {
      if (a.channel !== b.channel) {
        return a.channel === 'LPC' ? -1 : 1;
      }
      return b.bookingsCount - a.bookingsCount;
    });

  return { rollup, pctLpc, pctLpp, suppliers };
}

/**
 * Merges each chain's own daily trend into one series — safe to sum since
 * chains are disjoint properties and views are already deduped server-side.
 */
export function mergeDailyTrends(
  trends: ReadonlyArray<ReadonlyArray<ChainLpcLppSupplierDailyTrendPoint>>,
): Array<ChainLpcLppSupplierDailyTrendPoint> {
  const byDate = new Map<
    string,
    { lpc_bookings: number; lpp_bookings: number; lpc_views: number | null; lpp_views: number | null }
  >();
  for (const daily of trends) {
    for (const point of daily) {
      const existing = byDate.get(point.sale_date) ?? {
        lpc_bookings: 0,
        lpp_bookings: 0,
        lpc_views: null,
        lpp_views: null,
      };
      existing.lpc_bookings += point.lpc_bookings;
      existing.lpp_bookings += point.lpp_bookings;
      if (point.lpc_views != null) {
        existing.lpc_views = (existing.lpc_views ?? 0) + point.lpc_views;
      }
      if (point.lpp_views != null) {
        existing.lpp_views = (existing.lpp_views ?? 0) + point.lpp_views;
      }
      byDate.set(point.sale_date, existing);
    }
  }
  return Array.from(byDate.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([sale_date, point]) => ({ sale_date, ...point }));
}

export function errMsg(err: unknown): string {
  if (!err) {
    return '';
  }
  return err instanceof Error ? err.message : JSON.stringify(err);
}

/** Aggregates a per-chain useQueries fan-out, naming which chains failed since the rest still render. */
export function aggregateFanOutQueryState(
  chainNames: ReadonlyArray<string>,
  queries: ReadonlyArray<{ isFetching: boolean; isError: boolean; error: unknown }>,
): { isFetching: boolean; isError: boolean; error: Error | undefined } {
  const isFetching = queries.some((q) => q.isFetching);
  const isError = queries.some((q) => q.isError);
  const failedChainNames = chainNames.filter((_, i) => queries[i].isError);
  const error = isError
    ? new Error(`${failedChainNames.join(', ')}: ${errMsg(queries.find((q) => q.isError)?.error)}`)
    : undefined;
  return { isFetching, isError, error };
}

export function presetKeyFor(indices: [number, number], calendarLength: number): string | null {
  const maxIdx = calendarLength - 1;
  if (maxIdx < 0 || indices[1] !== maxIdx) {
    return null;
  }
  const span = indices[1] - indices[0] + 1;
  return PERIOD_PRESETS.find((p) => p.days === span)?.key ?? null;
}
